import React, { useCallback, useEffect, useState } from 'react';
import { Element } from 'react-scroll';
import {
  SalvButton,
  SalvProgressCircle,
  SalvTable,
  SalvTablePaginationType,
  SalvTooltipWrapper,
  Size,
} from '@salvapp/ui-components';
import { InternalApiCasePerson } from '../../../api-internal';
import { useDetachCasePersons, useQueryCasePersonsPage } from './casePersonQueryActions';
import { useQueryAttributeMappings } from '../../../schema/mappings/mappingsActions';
import { formatDate } from '../../../common/datetime/FormattedDateTime';
import { useDispatch } from 'react-redux';
import { showConfirmationModal } from '../../../confirmation-modal/confirmationModalReducer';
import { showFeedback } from '../../../feedback/feedbackReducer';
import Has from '../../../auth/Has';
import { CAN_MODIFY_CASE } from '../../../auth/permissions';
import DataLoadingErrorFallback from '../../../common/errors/DataLoadingErrorFallback';
import { createColumnHelper } from '@tanstack/react-table';
import { getPersonPath, InternalNavigation } from '../../../navigation/helper';
import { useNavigate, createSearchParams } from 'react-router';
import { PersonLink } from '../../../common/PersonLink';
import { SearchParamKeys } from '../../../common/searchParamKeys';

interface CasePersonsProps {
  handle: string;
}

export const CASE_PERSONS_SCROLL_NAME = 'case-persons';
const PAGE_SIZE = 10;
export const LIMIT = 100;

const CasePersons = ({ handle }: CasePersonsProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);

  const {
    data: personsPage,
    isPending,
    isFetching,
    isError,
    error,
  } = useQueryCasePersonsPage({ handle, page, size: PAGE_SIZE });

  useEffect(() => {
    setSelectedRows([]);
  }, [personsPage]);

  const { data: attributeMappings } = useQueryAttributeMappings();
  const detachCasePersons = useDetachCasePersons();

  const [selectedRows, setSelectedRows] = useState<InternalApiCasePerson[]>([]);

  const mappings = attributeMappings?.map ?? {};

  const handleDetach = async () => {
    const count = selectedRows.length;
    const isConfirmed = await dispatch(
      showConfirmationModal({
        title: 'Detach persons?',
        description: `${count} person${count > 1 ? 's' : ''} will be detached from this case.`,
        confirmationText: 'Yes, detach',
      })
    );

    if (!isConfirmed) return;

    try {
      await detachCasePersons.mutateAsync({
        handle,
        externalIds: selectedRows.map((row) => row.person.id),
      });
      dispatch(showFeedback({ message: 'Persons detached successfully', success: true }));
      setSelectedRows([]);
    } catch {
      dispatch(showFeedback({ message: 'Failed to detach persons', success: false }));
    }
  };

  const columnHelper = createColumnHelper<InternalApiCasePerson>();

  const getColumns = useCallback(
    () => [
      columnHelper.display({
        id: 'name',
        header: 'Name',
        cell: ({ row }) => <PersonLink person={row.original.person} mappings={mappings} />,
      }),
      columnHelper.accessor('createdAt', {
        header: 'Attached',
        cell: ({ getValue, row }) => (
          <SalvTooltipWrapper
            tooltip={`${row.original.createdBy.name}, ${formatDate(getValue(), true)}`}
            placement='top'
          >
            {formatDate(getValue())}
          </SalvTooltipWrapper>
        ),
      }),
    ],
    [mappings]
  );

  if (isError) {
    return <DataLoadingErrorFallback message='Could not load Case Persons' error={error} />;
  }

  return (
    <Element name={CASE_PERSONS_SCROLL_NAME}>
      {isPending ? (
        <SalvProgressCircle ariaLabel='Loading Persons' />
      ) : (
        <SalvTable<InternalApiCasePerson>
          title='Persons'
          columns={getColumns()}
          data={personsPage.content}
          selectedRows={personsPage.content.length ? selectedRows : undefined}
          setSelectedRows={setSelectedRows}
          isLoading={isFetching}
          emptyMessage='No Persons attached to this case'
          hover
          expandable
          rowClick={({ original }) => navigate(getPersonPath(original.person.id))}
          pagination={{
            type: SalvTablePaginationType.PAGINATION,
            total: personsPage.total,
            state: { pageIndex: page, pageSize: PAGE_SIZE },
            setPagination: ({ pageIndex }) => setPage(pageIndex),
          }}
          actions={
            <Has allPermissions={[CAN_MODIFY_CASE]}>
              <div style={{ display: 'flex', gap: '8px' }}>
                {selectedRows.length > 0 && (
                  <SalvButton
                    variant='muted'
                    size={Size.SMALL}
                    onClick={handleDetach}
                    label='Detach Selected'
                    disabled={detachCasePersons.isPending}
                  />
                )}
                <SalvButton
                  variant='secondary'
                  size={Size.SMALL}
                  onClick={() =>
                    navigate({
                      pathname: InternalNavigation.personSearch,
                      search: createSearchParams({
                        [SearchParamKeys.caseHandle]: handle,
                      }).toString(),
                    })
                  }
                  label='Attach Persons'
                />
              </div>
            </Has>
          }
        />
      )}
    </Element>
  );
};

export default CasePersons;
